import { i18n } from '@fs/zion-locale'
import { createCoupleRelation, gedcomBirth, gedcomDeath, gedcomGiven, gedcomMarriage, gedcomSurname } from './helpers'

export function personDemunge(person) {
  if (!person) return null
  return vitalsDemunge(person)
}

export function getCloseRelationString(person) {
  const { pathSummary, isSpouse, sex } = person
  let relation = ''

  // Get relation based spouse's sex
  if (isSpouse) {
    if (sex?.toLowerCase() === 'male') relation = i18n.t('common.close-relation.husband', 'Husband')
    else if (sex?.toLowerCase() === 'female') relation = i18n.t('common.close-relation.wife', 'Wife')
    else relation = i18n.t('relation.option.spouse', 'Spouse')
  }

  // Get relation based on pathSummary
  else if (pathSummary?.match(/^[mf]{2}m.$/i)) relation = i18n.t('common.close-relation.grandfather', 'Grandfather')
  else if (pathSummary?.match(/^[mf]{2}f.$/i)) relation = i18n.t('common.close-relation.grandmother', 'Grandmother')
  else if (pathSummary?.match(/^[mf]m.$/i)) relation = i18n.t('common.close-relation.father', 'Father')
  else if (pathSummary?.match(/^[mf]f.$/i)) relation = i18n.t('common.close-relation.mother', 'Mother')
  else if (pathSummary?.match(/^[mf]{2}.m$/i)) relation = i18n.t('common.close-relation.brother', 'Brother')
  else if (pathSummary?.match(/^[mf]{2}.f$/i)) relation = i18n.t('common.close-relation.sister', 'Sister')
  else if (pathSummary?.match(/^[mf].m$/i)) relation = i18n.t('common.close-relation.son', 'Son')
  else if (pathSummary?.match(/^[mf].f$/i)) relation = i18n.t('common.close-relation.daughter', 'Daughter')

  return relation
}

export function personMunge(person) {
  if (!person) return null

  const mungedPerson = vitalsMunge(person)
  mungedPerson.closeRelationString = getCloseRelationString(mungedPerson)
  return mungedPerson
}

function getVitalLikeData(person, vitalType) {
  return !person[`${vitalType}likeGedcomType`]
    ? null
    : {
        date: person[`${vitalType}likeDate`],
        place: person[`${vitalType}likePlace`],
        type: person[`${vitalType}likeGedcomType`],
      }
}

const gedcomTypes = {
  birth: gedcomBirth,
  death: gedcomDeath,
  marriage: gedcomMarriage,
}

export function getDemungedVitals(person, vitalType) {
  return {
    date: !person[`${vitalType}Date`]
      ? null
      : {
          original: person[`${vitalType}Date`]?.standard?.originalText ?? person[`${vitalType}Date`]?.primaryText,
          formal: person[`${vitalType}Date`]?.standard?.formalText,
          normalized: !person[`${vitalType}Date`]?.standard
            ? []
            : [
                {
                  lang: person[`${vitalType}DatePreferredLocale`] ?? i18n.language,
                  value: person[`${vitalType}Date`]?.standard?.standardizedText,
                },
              ],
        },
    place: !person[`${vitalType}Place`]
      ? null
      : {
          original: person[`${vitalType}Place`]?.primaryText,
          id: person[`${vitalType}Place`]?.standard?.id,
          names: !person[`${vitalType}Place`]?.standard
            ? []
            : [
                {
                  lang: person[`${vitalType}PlacePreferredLocale`] ?? i18n.language,
                  value: person[`${vitalType}Place`]?.standard?.standardizedText,
                },
              ],
        },
    type: gedcomTypes[vitalType],
  }
}

export function vitalsDemunge(person) {
  const demungedPerson = { ...person }

  if (!demungedPerson.lifespanBegin) {
    demungedPerson.lifespanBegin = !(person.birthDate || person.birthPlace)
      ? getVitalLikeData(person, 'birth')
      : getDemungedVitals(person, 'birth')
  }
  if (!demungedPerson.lifespanEnd) {
    demungedPerson.lifespanEnd = !(person.deathDate || person.deathPlace)
      ? getVitalLikeData(person, 'death')
      : getDemungedVitals(person, 'death')
  }

  if (!demungedPerson.marriage) {
    demungedPerson.marriage = !(person.marriageDate || person.marriagePlace)
      ? getVitalLikeData(person, 'marriage')
      : getDemungedVitals(person, 'marriage')
  }

  // Clean up data before sending it to the backend
  delete demungedPerson.birthlikeGedcomType
  delete demungedPerson.birthDatePreferredLocale
  delete demungedPerson.birthlikeDate
  delete demungedPerson.birthDate
  delete demungedPerson.birthPlacePreferredLocale
  delete demungedPerson.birthlikePlace
  delete demungedPerson.birthPlace
  delete demungedPerson.deathlikeGedcomType
  delete demungedPerson.deathDatePreferredLocale
  delete demungedPerson.deathlikeDate
  delete demungedPerson.deathDate
  delete demungedPerson.deathPlacePreferredLocale
  delete demungedPerson.deathlikePlace
  delete demungedPerson.deathPlace

  return demungedPerson
}

/**
 * @describe Munges lifspan begin/end place data into birth/death place data.
 * @param {string} lifespanType - Possible values: ['lifespanBegin', 'lifespanEnd']
 * @returns {object} - Munged birth/death place data object.
 */
export function getMungedVitalPlace(person, lifespanType) {
  if (!person[lifespanType]?.place) return null
  return {
    isStandard: !!person[lifespanType]?.place?.id,
    preferredLocale: person[lifespanType]?.place?.normalized?.[0]?.lang ?? i18n.language,
    primaryText: person[lifespanType]?.place?.names?.length
      ? person[lifespanType]?.place?.names?.[0]?.value
      : person[lifespanType]?.place?.original,
    standard: !person[lifespanType]?.place?.id
      ? null
      : {
          id: person[lifespanType]?.place?.id,
          standardizedText: person[lifespanType]?.place?.names?.length
            ? person[lifespanType]?.place?.names?.[0]?.value
            : '',
        },
  }
}

export function getMungedVitalDate(person, lifespanType) {
  if (!person[lifespanType]?.date) return null
  return {
    isStandard: !!person?.[lifespanType]?.date?.formal,
    preferredLocale: person?.[lifespanType]?.date?.normalized?.[0]?.lang ?? i18n.language,
    primaryText: person?.[lifespanType]?.date?.original,
    standard: !person?.[lifespanType]?.date?.formal
      ? null
      : {
          formalText: person?.[lifespanType]?.date?.formal,
          originalText: person?.[lifespanType]?.date?.original,
          standardizedText: person?.[lifespanType]?.date?.normalized?.[0]?.value,
        },
  }
}

export function vitalsMunge(person) {
  // Return person object if there are no vitals to munge
  if (!person?.lifespanBegin && !person?.lifespanEnd)
    return {
      birthDate: null,
      birthPlace: null,
      deathDate: null,
      deathPlace: null,
      ...person,
    }

  // Return person object with munged properties
  const mungedPerson = {
    ...person,
    birthDatePreferredLocale: person.lifespanBegin?.date?.normalized?.[0]?.lang ?? i18n.language,
    birthPlacePreferredLocale: person.lifespanBegin?.place?.normalized?.[0]?.lang ?? i18n.language,
    deathDatePreferredLocale: person.lifespanEnd?.date?.normalized?.[0]?.lang ?? i18n.language,
    deathPlacePreferredLocale: person.lifespanEnd?.place?.normalized?.[0]?.lang ?? i18n.language,
  }

  // Either store birth date and place OR birth-like date and place NOT both
  if (person.lifespanBegin?.type === gedcomBirth) {
    mungedPerson.birthDate = getMungedVitalDate(person, 'lifespanBegin')
    mungedPerson.birthPlace = getMungedVitalPlace(person, 'lifespanBegin')
  } else if (person.lifespanBegin?.type) {
    mungedPerson.birthlikeGedcomType = person.lifespanBegin?.type
    mungedPerson.birthLikeDate = person.lifespanBegin?.date
    mungedPerson.birthLikePlace = person.lifespanBegin?.place
  } else {
    mungedPerson.birthDate = null
    mungedPerson.birthPlace = null
  }

  // Either store death date and place OR death-like date and place NOT both
  if (person.lifespanEnd?.type === gedcomDeath) {
    mungedPerson.deathDate = getMungedVitalDate(person, 'lifespanEnd')
    mungedPerson.deathPlace = getMungedVitalPlace(person, 'lifespanEnd')
  } else if (person.lifespanEnd?.type) {
    mungedPerson.deathlikeGedcomType = person.lifespanEnd?.type
    mungedPerson.deathlikeDate = person.lifespanEnd?.date
    mungedPerson.deathlikePlace = person.lifespanEnd?.place
  } else {
    mungedPerson.deathDate = null
    mungedPerson.deathPlace = null
  }

  // Remove old, redundant properties
  delete mungedPerson.lifespanBegin
  delete mungedPerson.lifespanEnd

  return mungedPerson
}

export function memberDataMunge(memberData) {
  const officialName =
    memberData?.names?.find((name) => name?.nameType?.toLowerCase() === 'official') ?? memberData?.names?.[0]
  return {
    nameForms: [
      {
        fullText: officialName?.certificateFormattedName ?? '',
        parts: [
          { type: gedcomGiven, value: officialName?.givenName ?? '' },
          { type: gedcomSurname, value: officialName?.familyName ?? '' },
        ],
      },
    ],
    uuid: memberData?.personUuid,
    sex: memberData?.sexCode,
  }
}

export function membershipDataMunge(membershipData) {
  const date = new Date(`${membershipData?.birth?.date}T00:00`)
  return {
    ...membershipData,
    recordName: (membershipData?.names?.find((name) => name?.nameType === 'Official') ?? membershipData?.names?.[0])
      ?.certificateFormattedName,
    birthDate: date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' }),
  }
}

export function mungeTreeData(treeData) {
  const mungedTreeData = structuredClone(treeData.data)

  // Step 1: Create `memberPerson` object from treeData and clean up unnecessary properties
  const memberPerson = separateMemberPersonFromTreeData(mungedTreeData)

  // Step 2: Munge persons
  mungeTreePersons(mungedTreeData)

  // Step 3: Munge parentChildRelationships to the expected data structure
  mungeTreePCRelationships(mungedTreeData, memberPerson?.id)

  // Step 4: Remove duplicate Parent-Child Relationships in `parentChildRelationships`
  removeDuplicatePCRelationships(mungedTreeData)

  // Step 5: Munge coupleRelationship
  mungeTreeCoupleRelationships(mungedTreeData)

  return mungedTreeData
}

// A function for Step 1: Remove the memberPerson from the treeData and clean up unnecessary properties.
// NOTE: This function mutates the `mungeTreeData` object.
// Returns the `memberPerson` object.
function separateMemberPersonFromTreeData(mungedTreeData) {
  delete mungedTreeData.baptism
  delete mungedTreeData.confirmation
  delete mungedTreeData.marriage

  // Create `memberPerson` object from treeData
  const memberPerson = vitalsMunge(mungedTreeData)
  memberPerson.idType = 'TREE'
  memberPerson.isMemberPerson = true // Used for setting `memberPersonId` in metadata
  delete memberPerson?.relatives
  delete memberPerson?.couplesRelationships
  delete memberPerson?.parentChildRelationshipsAsParent
  delete memberPerson?.parentsPairings

  // Shape root of treeData into WorkingPersonContainer
  mungedTreeData.persons = Object.values(mungedTreeData.relatives)
  mungedTreeData.persons.push(memberPerson)
  mungedTreeData.parentChildRelationships = [
    ...mungedTreeData.parentsPairings,
    ...mungedTreeData.parentChildRelationshipsAsParent,
  ]
  delete mungedTreeData.parentsPairings
  delete mungedTreeData.parentChildRelationshipsAsParent
  delete mungedTreeData.relatives
  delete mungedTreeData.id
  delete mungedTreeData.lifespanBegin
  delete mungedTreeData.lifespanEnd
  delete mungedTreeData.living
  delete mungedTreeData.nameForms
  delete mungedTreeData.sex

  return memberPerson
}

// A function for Step 2: Munge the persons of the treeData.
// NOTE: This function mutates the `mungeTreeData` object.
function mungeTreePersons(mungedTreeData) {
  mungedTreeData.persons.forEach((person, index, array) => {
    // Move each coupleRelationships, parentChildRelationshipsAsParent, and parentsPairings to the top level
    if (Array.isArray(person.couplesRelationships)) {
      mungedTreeData.couplesRelationships.push(...person.couplesRelationships)
    }
    if (Array.isArray(person.parentChildRelationshipsAsParent)) {
      mungedTreeData.parentChildRelationships.push(...person.parentChildRelationshipsAsParent)
    }
    if (Array.isArray(person.parentsPairings)) {
      mungedTreeData.parentChildRelationships.push(
        ...person.parentsPairings.map((pairing) => ({ ...pairing, childId: person.id }))
      )
    }
    delete array[index].marriage // We keep the marriage facts in the coupleRelationships
    delete array[index].relatives
    delete array[index].couplesRelationships
    delete array[index].parentsPairings
    delete array[index].parentChildRelationshipsAsParent
    array[index] = vitalsMunge(person)
    array[index].idType = 'TREE'
  })
}

// A function for Step 3: Munge the ParentChildRelationships of the treeData.
// NOTE: This function mutates the `mungeTreeData` object.
function mungeTreePCRelationships(mungedTreeData, memberPersonId) {
  mungedTreeData.parentChildRelationships.forEach((pairing, index, array) => {
    array[index].child = {
      id: pairing.childId ?? memberPersonId,
      idType: 'TREE',
    }
    array[index].parent1 = {
      id: pairing.personId1 ?? pairing.selfId,
      idType: 'TREE',
    }
    array[index].parent2 = {
      id: pairing.personId2 ?? pairing.otherParentId,
      idType: 'TREE',
    }
    delete array[index].personId1
    delete array[index].personId2
    delete array[index].selfId
    delete array[index].otherParentId
    delete array[index].childId

    // Add persons from single-parent parentChildRelationships
    if (!pairing.parent1.id) {
      const id = `${pairing.parent2.id}-spouse`
      array[index].parent1.id = id
      array[index].parent1.idType = 'TEMPORARY'
      const parent2PersonObj = mungedTreeData.persons.find((person) => person.id === pairing.parent2.id)
      mungedTreeData.persons.push({
        id,
        idType: 'TEMPORARY',
        sex: parent2PersonObj.sex === 'MALE' ? 'FEMALE' : 'MALE',
      })
    } else if (!pairing.parent2.id) {
      const id = `${pairing.parent1.id}-spouse`
      array[index].parent2.idType = 'TEMPORARY'
      array[index].parent2.id = id
      const parent1PersonObj = mungedTreeData.persons.find((person) => person.id === pairing.parent1.id)
      mungedTreeData.persons.push({
        id,
        idType: 'TEMPORARY',
        sex: parent1PersonObj.sex === 'MALE' ? 'FEMALE' : 'MALE',
      })
    }
  })
}

// A function for Step 4: Remove the duplicate ParentChildRelationships of the treeData.
// NOTE: This function mutates the `mungeTreeData` object.
function removeDuplicatePCRelationships(mungedTreeData) {
  mungedTreeData.parentChildRelationships = mungedTreeData.parentChildRelationships.filter(
    (pairing, index, array) =>
      !array
        .slice(index + 1)
        .some(
          (otherPairing) =>
            pairing.child.id === otherPairing.child.id &&
            ((pairing.parent1.id === otherPairing.parent1.id && pairing.parent2.id === otherPairing.parent2.id) ||
              (pairing.parent1.id === otherPairing.parent2.id && pairing.parent2.id === otherPairing.parent1.id))
        )
  )
}

// A function for Step 5: Munge the coupleRelationships of the treeData.
// NOTE: This function mutates the `mungeTreeData` object.
function mungeTreeCoupleRelationships(mungedTreeData) {
  mungedTreeData.coupleRelationships = mungedTreeData.couplesRelationships
  delete mungedTreeData.couplesRelationships
  mungedTreeData.coupleRelationships.forEach((coupleRel, index, array) => {
    array[index].marriage = coupleRel.facts.find((fact) => fact.type === gedcomMarriage) ?? { type: gedcomMarriage }
    delete array[index].facts
    array[index].person1 = { id: coupleRel.selfId, idType: 'TREE' }
    array[index].person2 = { id: coupleRel.otherId, idType: 'TREE' }
    delete array[index].selfId
    delete array[index].otherId
  })

  // Make sure parentChildRelationships are already reflected in the coupleRelationships array
  mungedTreeData.parentChildRelationships.forEach((PCRel) => {
    const person1Id = PCRel.parent1.id
    const person2Id = PCRel.parent2.id
    const isAlreadyInCouplesArray = mungedTreeData.coupleRelationships.some(
      ({ person1, person2 }) =>
        [person1Id, person2Id].includes(person1.id) && [person1Id, person2Id].includes(person2.id)
    )
    if (!isAlreadyInCouplesArray) {
      const newCoupleRel = createCoupleRelation(person1Id, person2Id)
      if (newCoupleRel) mungedTreeData.coupleRelationships.push(newCoupleRel)
    }
  })
}
